import type * as React from 'react'

import { Button, CoachMark, Flex, NewBadge } from '@buffer-mono/popcorn'

import { useDismissableBanner } from '~publish/hooks/useDismissableBanner'

import styles from './MentionsAvailableCoachmark.module.css'

import type { JSX } from 'react'

const BANNER_ID = 'mentions-available-coachmark'

type MentionsAvailableCoachmarkProps = {
  /**
   * The element the coachmark points at — the Mentions tab. Rendered untouched
   * when the coachmark isn't showing, so callers can wrap unconditionally.
   */
  children: React.ReactNode
  /**
   * Whether any channel in scope can actually surface mentions. Announcing the
   * feature to someone who can't use it yet would just be noise.
   */
  mentionsSupported: boolean
}

/**
 * One-time announcement that mentions have arrived, pointing at the Mentions
 * tab. Dismissal is stored on the account, so it shows once per user across
 * devices and views rather than once per page.
 *
 * The copy names the launch channel, so it needs revisiting whenever mentions
 * ship for another one — and the whole coachmark can go once the launch has
 * been out long enough that nobody needs telling.
 */
export function MentionsAvailableCoachmark({
  children,
  mentionsSupported,
}: MentionsAvailableCoachmarkProps): JSX.Element {
  const banner = useDismissableBanner(BANNER_ID, { trackDismiss: true })

  if (!mentionsSupported || !banner.isActive) {
    return <>{children}</>
  }

  return (
    <CoachMark defaultOpen onDismiss={banner.dismiss} dismissTimeout={null}>
      <CoachMark.Overlay>
        <CoachMark.Spotlight size="medium" />
        {children}
      </CoachMark.Overlay>
      <CoachMark.Content className={styles.content} showCloseIcon={false}>
        <CoachMark.Title>
          <Flex align="center" gap="xs">
            <NewBadge />
            Mentions available for X
          </Flex>
        </CoachMark.Title>
        <CoachMark.Description>
          Now you can see mentions from your X channels. We&apos;re actively
          working on adding more channels.
        </CoachMark.Description>
        <CoachMark.Footer>
          <CoachMark.Dismiss>
            <Button variant="primary">Got It</Button>
          </CoachMark.Dismiss>
        </CoachMark.Footer>
      </CoachMark.Content>
    </CoachMark>
  )
}
